import { Children, ReactElement, ReactNode, cloneElement, isValidElement, useEffect, useRef, useState } from "react";

/**
 * SYSTEM_DESIGN.md §6.3 — "secciones colapsables para formularios complejos"
 * (Crear Evento, Crear Taller, Crear Venue).
 *
 * La numeración no está en el spec; sale del mockup y le da sentido de orden y
 * progreso a un formulario largo. El número lo inyecta <FormSections> a partir
 * de la POSICIÓN, no se pasa a mano: así una sección condicional o reordenada
 * no deja la numeración con huecos.
 *
 * `icon`, `htmlFor` y `actions` vienen del FormSection local que tenía
 * SupportArticleForm, que se dio de baja al unificar: eran tres implementaciones
 * distintas del mismo concepto conviviendo en el repo.
 */
interface FormSectionProps {
  title: string;
  children: ReactNode;
  /** Ícono PrimeIcons a la izquierda del título. Ej: "pi-info-circle". */
  icon?: string;
  /**
   * Cuando la sección es un único campo, el encabezado hace de label de ese
   * campo en vez de ser un heading suelto.
   */
  htmlFor?: string;
  /** Acciones a la derecha del encabezado, antes del chevron. */
  actions?: ReactNode;
  /**
   * Acción pegada al título (ej. el `+` de alta de la sección), igual que el
   * `+` junto al título de página (§6.1). Queda lejos del chevron de colapsar,
   * así no se toca uno queriendo tocar el otro.
   */
  titleAction?: ReactNode;
  /** Lo inyecta <FormSections>. */
  number?: number;
  /** Lo inyecta <FormSections> para la primera; explícito acá, gana. */
  defaultOpen?: boolean;
  className?: string;
  validationAttempt?: number;
}

export const FormSection = ({
  title,
  children,
  icon,
  htmlFor,
  actions,
  titleAction,
  number,
  defaultOpen = false,
  className = "",
  validationAttempt = 0,
}: FormSectionProps) => {
  const [open, setOpen] = useState(defaultOpen);
  const sectionRef = useRef<HTMLDivElement>(null);
  const toggle = () => setOpen((o) => !o);

  useEffect(() => {
    const section = sectionRef.current;
    if (!validationAttempt || !section) return;
    const invalidSelector = '.p-invalid, .p-error, [aria-invalid="true"]';
    if (!section.querySelector(invalidSelector)) return;
    setOpen(true);
    const firstInvalid = section.closest("form")?.querySelector(invalidSelector);
    if (firstInvalid && section.contains(firstInvalid)) {
      section.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [validationAttempt]);

  const heading = (
    <>
      {number !== undefined && (
        <span
          className="flex align-items-center justify-content-center border-circle text-xs font-bold flex-shrink-0"
          style={{
            width: "24px",
            height: "24px",
            background: "var(--brand-primary)",
            color: "var(--brand-primary-text)",
          }}
        >
          {number}
        </span>
      )}
      {icon && <i className={`pi ${icon} text-primary`} />}
      {title}
      {titleAction && (
        // No togglea la sección al hacerle clic.
        <span
          className="flex align-items-center ml-2"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          role="presentation"
        >
          {titleAction}
        </span>
      )}
    </>
  );

  return (
    <div ref={sectionRef} className={`form-section ${className}`}>
      <div
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggle();
          }
        }}
        className="form-section__head flex align-items-center justify-content-between gap-2"
      >
        {htmlFor ? (
          <label htmlFor={htmlFor} className="flex align-items-center gap-2 font-bold cursor-pointer m-0">
            {heading}
          </label>
        ) : (
          <span className="flex align-items-center gap-2 font-bold">{heading}</span>
        )}

        <span className="flex align-items-center gap-2">
          {/* Las acciones no deben togglear la sección al hacerles clic. */}
          {actions && (
            <span onClick={(e) => e.stopPropagation()} role="presentation">
              {actions}
            </span>
          )}
          <i
            className="pi pi-chevron-down"
            style={{
              transition: "transform var(--sidebar-transition)",
              transform: open ? "rotate(180deg)" : "none",
            }}
          />
        </span>
      </div>
      {/* El contenido queda SIEMPRE montado: animar una altura desconocida
          necesita el elemento en el DOM (grid-template-rows 0fr → 1fr, ver
          layout.css). De paso los campos de una sección cerrada siguen
          existiendo, así que Formik los valida y se los puede enfocar. */}
      <div className={`form-section__body ${open ? "form-section__body--open" : ""}`}>
        <div className="form-section__inner">
          <div className="form-section__content">{children}</div>
        </div>
      </div>
    </div>
  );
};

/**
 * Numera las <FormSection> hijas por posición y abre la primera.
 *
 * Con todas cerradas, un formulario de 6 secciones abre sin un solo campo a la
 * vista; el mockup deja la 1 abierta. Un `defaultOpen` explícito en el call site
 * gana sobre esto, para poder abrir otra cuando haga falta.
 */
export const FormSections = ({ children, validationAttempt }: { children: ReactNode; validationAttempt?: number }) => {
  let n = 0;
  return (
    <>
      {Children.map(children, (child) => {
        if (!isValidElement(child)) return child;
        n += 1;
        const el = child as ReactElement<FormSectionProps>;
        return cloneElement(el, {
          number: n,
          defaultOpen: el.props.defaultOpen ?? n === 1,
          validationAttempt,
        });
      })}
    </>
  );
};
